import { FieldType, UUID } from '@indigohive/cogfy-types'
import { Button } from '../Button'
import { PlusIcon, TrashIcon } from 'lucide-react'
import { ChatMiddlewareConditionSourceSelect } from './subcomponents/ChatMiddlewareConditionSourceSelect'
import { ChatMiddlewareConditionOperatorSelect } from './subcomponents/ChatMiddlewareConditionOperatorSelect'
import { ChatMiddlewareConditionValueInput } from './subcomponents/ChatMiddlewareConditionValueInput'
import { useTranslation } from 'react-i18next'
import { FieldPropertyMentionValue } from '../FieldPropertyMention'
import { useQuery } from '@tanstack/react-query'
import { useCogfy } from '../../hooks'

export type ChatMiddlewareConditionSource =
  { type: 'content' } |
  { type: 'content_type' } |
  { type: 'field', field: { id: string, type: FieldType } } |
  { type: 'template', field: { id: string, type: FieldType, property: string } }

export type ChatMiddlewareConditionValue =
  string |
  number |
  boolean |
  null |
  FieldPropertyMentionValue

export type ChatMiddlewareConditionItem = {
  source: ChatMiddlewareConditionSource
  operator: string
  value: ChatMiddlewareConditionValue
}

export type ChatMiddlewareConditionFormProps = {
  fields?: {
    id: UUID
    name: string
    type: FieldType
  }[]
  value: Record<string, any>
  onChange: (value: Record<string, any>) => void
}

function mapConditionToItems (condition: Record<string, any>): ChatMiddlewareConditionItem[] {
  if (condition.type !== 'and' || !Array.isArray(condition.data?.value)) {
    return []
  }

  return condition.data.value.map((item: Record<string, any>) => {
    if (item.type === 'content_type') {
      return {
        source: { type: 'content_type' },
        operator: item.data?.operator ?? 'equals',
        value: item.data?.value
      }
    } else if (item.type === 'content') {
      return {
        source: { type: 'content' },
        operator: item.data?.operator ?? 'equals',
        value: item.data?.value
      }
    } else if (item.type === 'template') {
      return {
        source: {
          type: 'template',
          field: {
            id: item.data?.source?.fieldId,
            type: item.data?.source?.fieldType,
            property: item.data?.source?.property
          },
          operator: item.data?.operator,
          value: item.data?.value
        },
        operator: item.data?.operator ?? 'equals',
        value: item.data?.value
      }
    } else {
      return {
        source: {
          type: 'field',
          field: {
            id: item.data?.id,
            type: item.data?.type
          }
        },
        operator: item.data?.operator ?? 'equals',
        value: item.data?.value
      }
    }
  })
}

function mapItemsToCondition (items: ChatMiddlewareConditionItem[]): Record<string, any> {
  return {
    type: 'and',
    data: {
      value: items.map(item => {
        if (item.source.type === 'content_type') {
          return {
            type: item.source.type,
            data: {
              operator: item.operator,
              value: item.value
            }
          }
        }

        if (item.source.type === 'template') {
          return {
            type: 'template',
            data: {
              source: {
                fieldId: item.source.field.id,
                fieldType: item.source.field.type,
                property: item.source.field.property
              },
              operator: item.operator,
              value: item.value
            }
          }
        }

        if (item.source.type === 'content') {
          return {
            type: 'content',
            data: {
              operator: item.operator,
              value: item.value
            }
          }
        }

        return {
          type: 'field',
          data: {
            id: item.source.field.id,
            type: item.source.field.type,
            operator: item.operator,
            value: item.value
          }
        }
      })
    }
  }
}

export function ChatMiddlewareConditionForm (props: ChatMiddlewareConditionFormProps) {
  const { fields = [], value, onChange } = props
  const { t } = useTranslation()
  const cogfy = useCogfy()

  const items: ChatMiddlewareConditionItem[] = mapConditionToItems(value)

  const handleChange = (items: ChatMiddlewareConditionItem[]) => {
    onChange(mapItemsToCondition(items))
  }

  const getFieldProperties = useQuery({
    queryKey: ['getFieldProperties'],
    queryFn: ({ signal }) => cogfy.getFieldProperties({ signal })
  })

  const handleAdd = () => {
    handleChange([
      ...items,
      {
        source: { type: 'content_type' },
        operator: 'equals',
        value: 'text'
      }
    ])
  }

  return (
    <div className="my-2 w-full">
      <div className="flex items-center">
        <div className="grow text-sm">
          {t('editFieldDrawer:Conditions')}
        </div>
        <Button
          ghost
          square
          size="sm"
          onClick={() => { handleAdd() }}
        >
          <PlusIcon size={16} />
        </Button>
      </div>
      {items.length === 0 && (
        <div className="text-sm opacity-40">
          {t('editFieldDrawer:No conditions set')}
        </div>
      )}
      {items.map((item, index) => (
        <div key={index}>
          <div className="flex flex-row gap-2 py-1 justify-between">
            <ChatMiddlewareConditionSourceSelect
              fields={fields}
              value={item.source}
              fieldProperties={getFieldProperties.data}
              onChange={source => {
                const newValue = [...items]
                newValue[index] = {
                  ...newValue[index],
                  source,
                  value: source.type === 'content_type' ? 'text' : ''
                }
                handleChange(newValue)
              }}
            />
            <ChatMiddlewareConditionOperatorSelect
              source={item.source}
              value={item.operator}
              fields={fields}
              fieldProperties={getFieldProperties.data}
              onChange={operator => {
                const newValue = [...items]
                newValue[index] = { ...newValue[index], operator }
                handleChange(newValue)
              }}
            />
            {item.operator !== 'is_empty' && item.operator !== 'is_not_empty' && (
              <div className="w-full self-center">
                <ChatMiddlewareConditionValueInput
                  source={item.source}
                  value={item.value}
                  fields={fields}
                  fieldProperties={getFieldProperties.data}
                  onChange={value => {
                    const newValue = [...items]
                    newValue[index] = { ...newValue[index], value }
                    handleChange(newValue)
                  }}
                />
              </div>
            )}
            <div>
              <Button
                ghost
                square
                size="sm"
                onClick={() => {
                  handleChange(items.filter((_, i) => i !== index))
                }}
              >
                <TrashIcon size={16} />
              </Button>
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}
